import type { GamePlayerProjectionTrading } from '@library/types/trading';
import type { LeagueTeamPlayer } from '@library/types/player';
import { isAdjustedOdds } from '@library/utils';
import { usePublishSimulationMutation } from '@bo-services/simulationsApi';

export const usePublishSimulation = () => {
  const [
    publishSimulation,
    {
      error: publishSimulationError,
      isLoading,
      isError: isPublishSimulationError,
      isSuccess,
      isUninitialized: isPublishSimulationUninitialized,
      reset: resetPublishSimulation
    }
  ] = usePublishSimulationMutation();

  const isPublishSimulationSuccess =
    !isPublishSimulationUninitialized && isSuccess;

  const isPublishSimulationLoading =
    isPublishSimulationUninitialized && isLoading;

  const handlePublishSimulation = async ({
    gameId,
    leagueId,
    modifiedProjections,
    players,
    pricingStrategy,
    simulationId
  }: {
    gameId: string;
    leagueId: string;
    modifiedProjections: Array<GamePlayerProjectionTrading>;
    players: Array<LeagueTeamPlayer>;
    pricingStrategy: 'LEGACY_PRICING' | 'SIMULATION_BASED_PRICING';
    simulationId: string;
  }) => {
    const createUpdateProjections = modifiedProjections.flatMap(
      (projection) => {
        const {
          autoTrade,
          boardTime,
          description,
          durationId,
          foreignId,
          gameId: projectionGameId,
          inGame,
          leagueId: projectionLeagueId = leagueId,
          maxProbabilityOfMore,
          minProbabilityOfMore,
          playerId: projectionPlayerId,
          playerImage,
          playerName,
          position,
          projectionId,
          projectionValue,
          simsProbabilityOfMore,
          startTime,
          displayTime,
          targetProbabilityOfMore,
          team,
          teamName
        } = projection;

        const player = players.find(
          (prospectivePlayer) => prospectivePlayer.foreign_id === foreignId
        );

        if (!player) {
          return [];
        }

        return [
          {
            players: projectionPlayerId
              ? [player]
              : [
                  {
                    foreign_id: foreignId,
                    image_url: playerImage,
                    league_id: Number(projectionLeagueId),
                    name: playerName,
                    position,
                    sportsradar_id: true, // this needs to be dynamic once other sources come into player
                    team,
                    team_name: teamName
                  }
                ],
            projections: [
              {
                id: projectionId ? Number(projectionId) : null,
                adjusted_odds: isAdjustedOdds(simsProbabilityOfMore),
                auto_trade: autoTrade,
                board_time: boardTime,
                description: description || 'TODO',
                duration_id: Number(durationId),
                game_id: projectionGameId,
                in_game: inGame,
                league_id: Number(projectionLeagueId),
                line_score: String(projectionValue),
                max_probability_of_more: maxProbabilityOfMore,
                min_probability_of_more: minProbabilityOfMore,
                sims_probability_of_more: simsProbabilityOfMore,
                rank: projection.rank ? Number(projection.rank) : 0,
                start_time: startTime,
                display_time: displayTime,
                stat_type_id: Number(projection.statTypeId),
                status: projection.status || 'pre_game',
                target_probability_of_more: targetProbabilityOfMore
              }
            ]
          }
        ];
      }
    );

    await publishSimulation({
      gameId,
      leagueId,
      pricingStrategy,
      projections_request: createUpdateProjections,
      simulationId
    }).catch(() => {});
  };

  return {
    handlePublishSimulation,
    publishSimulationError,
    isPublishSimulationError,
    isPublishSimulationLoading,
    isPublishSimulationSuccess,
    resetPublishSimulation
  };
};
